import { Box } from '@mui/material';
import { EditorProvider } from '@tiptap/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useAttachmentData from '../../hooks/messages/useAttachmentData';
import useAttachmentURL from '../../hooks/messages/useAttachmentUrl';

import { useSelector } from 'react-redux';
import useShortcuts from '../../hooks/shortcuts/useShortcuts.js';
import EditorHandler from './EditorHandler.jsx';
import { ExtendExtension } from './extensions/ExtendExtension.js';
import TemplateDisplay from './TemplateDisplay.jsx';
import { tiptapExtensions } from './tiptapExtensions';
import ToolbarEditor from './ToolbarEditor.jsx';
import { extractInlineImagesFromEditor } from './utils';
import { DynDataExtension } from './extensions/DyncDataExtension.js';
import useDynamicData from '../../hooks/template/useDynamicData.js';

const RichTextEditor = ({
  loadingSave,
  loadingSend,
  setIsLoadingSend,
  setIsLoadingSave,
  mutateTicket,
  mutate,
  ticket,
  draft,
  recipientType,
  subject,
  setSubject,
  message,
  setMessage,
  attachments,
  setAttachments,
  deleteAttachment,
  receiverList,
  handleCancelNewMessage,
  handleSaveNewMessage,
  handleSendMessage,
  onFileAdd,
  setIsEditorDirty,
  inlineAttachments,
  setInlineAttachments,
}) => {
  const profile = useSelector((state) => state?.login?.profile);
  const [attachmentsSize, setTotalAttachmentsSize] = useState(0);
  const [currentEditor, setCurrentEditor] = useState(null);
  const [editorHasChanged, setEditorHasChanged] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [isInlineAttLoaded, setIsInlineAttLoaded] = useState(false);
  const [_lastContent, setLastContent] = useState('');

  // Initialise les hooks pour la gestion des pièces jointes.
  const { getAttachmentUrl } = useAttachmentURL();
  const { getAttachmentData } = useAttachmentData();

  const { data } = useShortcuts(profile?._id);
  const { dynDatas } = useDynamicData({});
  const shortcutExtensionExtendRef = useRef(null);
  const shortcutExtensionDynDataRef = useRef(null);

  const currentExtension = useMemo(() => {
    const extend = ExtendExtension(data?.shortcuts || []);
    const dynData = DynDataExtension(dynDatas || []);

    shortcutExtensionExtendRef.current = extend;
    shortcutExtensionDynDataRef.current = dynData;
    return [...tiptapExtensions, extend, dynData];
  }, []);

  const handleLink = () => {
    const link = window.prompt('Ajouter un lien');
    if (link && currentEditor) {
      currentEditor.chain().focus().setLink({ href: link }).run();
    }
  };

  const handleEditorReady = (editor) => {
    setCurrentEditor(editor);
  };

  const handleEditorUpdate = ({ editor }) => {
    const inlineImages = extractInlineImagesFromEditor({ editor });
    const inlineFitered = inlineImages.filter(
      (inlineatt) => inlineatt.link !== 'pandora/logo-hoortrade.jpg'
    );
    if (inlineFitered.length < inlineAttachments.length) {
      const attachementToDelete = inlineAttachments.filter(
        (att) => !inlineFitered.some((inlineAtt) => inlineAtt.alt === att.alt)
      );
      console.log('[RichTextEditor] Attachements à supprimer:', {
        attachementToDelete,
      });
      setInlineAttachments(inlineFitered);
      setAttachments((prev) =>
        prev.filter((att) => att.link !== attachementToDelete.link)
      );
    }
    setMessage(editor.getHTML());

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const newTimer = setTimeout(() => {
      setEditorHasChanged(true);
      setIsEditorDirty(true);
      setIsInlineAttLoaded(false);
    }, 1500);

    setDebounceTimer(newTimer);
  };

  // Synchronise la prop `message` avec le contenu de l'éditeur Tiptap.
  useEffect(() => {
    if (currentEditor) {
      const currentEditorContent = currentEditor.getHTML();
      const newMessageContent = message || '';

      // Évite les mises à jour si l'éditeur est en focus ou si le contenu est identique.
      if (
        currentEditor.isFocused ||
        currentEditorContent === newMessageContent
      ) {
        return;
      }

      currentEditor.commands.setContent(newMessageContent, false);
      setLastContent(newMessageContent);
    }
  }, [message, currentEditor]);

  // Met à jour la taille totale des pièces jointes.
  useEffect(() => {
    const newSize = attachments.reduce((res, att) => {
      return res + Number(att?.size || 0);
    }, 0);

    console.log('[RichTextEditor] Mise à jour taille attachements:', {
      newSize,
      attachmentsCount: attachments.length,
      hasInlineAttachments: attachments.some((att) => !att.isAtt),
    });

    setTotalAttachmentsSize(newSize);
  }, [attachments]);

  // Nettoyage du timer de debounce à la suppression du composant.
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  useEffect(() => {
    if (
      data?.shortcuts &&
      shortcutExtensionExtendRef.current?.options?.setShortcuts
    ) {
      shortcutExtensionExtendRef.current.options.setShortcuts(data.shortcuts);
    }
  }, [data]);

  useEffect(() => {
    if (dynDatas && shortcutExtensionDynDataRef.current?.options?.setList) {
      shortcutExtensionDynDataRef.current.options.setList(dynDatas);
    }
  }, [dynDatas]);

  return (
    <Box className="editor-input">
      <Box className="editor-inner">
        <EditorProvider
          editorProps={{
            attributes: {
              class: 'tiptap',
            },
          }}
          onUpdate={handleEditorUpdate}
          slotBefore={
            <ToolbarEditor editor={currentEditor} handleLink={handleLink} />
          }
          slotAfter={
            <TemplateDisplay
              loadingSave={loadingSave}
              loadingSend={loadingSend}
              mutateTicket={mutateTicket}
              mutate={mutate}
              setIsLoadingSend={setIsLoadingSend}
              setIsLoadingSave={setIsLoadingSave}
              ticket={ticket}
              draft={draft}
              recipientType={recipientType}
              subject={subject}
              setSubject={setSubject}
              extensions={currentExtension}
              attachments={attachments}
              message={message}
              setMessage={setMessage}
              setAttachments={setAttachments}
              receiverList={receiverList}
              attachmentsSize={attachmentsSize}
              setTotalAttachmentsSize={setTotalAttachmentsSize}
              handleCancelNewMessage={handleCancelNewMessage}
              handleSaveNewMessage={handleSaveNewMessage}
              handleSendMessage={handleSendMessage}
              editorHasChanged={editorHasChanged}
              setEditorHasChanged={setEditorHasChanged}
              isInlineAttLoaded={isInlineAttLoaded}
              setIsInlineAttLoaded={setIsInlineAttLoaded}
              setIsEditorDirty={setIsEditorDirty}
              editor={currentEditor}
              getAttachmentData={getAttachmentData}
              getAttachmentUrl={getAttachmentUrl}
              inlineAttachments={inlineAttachments}
              setInlineAttachments={setInlineAttachments}
            />
          }
          extensions={currentExtension}
          content={message || ''}
        >
          <EditorHandler
            ticket={ticket}
            attachments={attachments}
            setAttachments={setAttachments}
            attachmentsSize={attachmentsSize}
            setTotalAttachmentsSize={setTotalAttachmentsSize}
            deleteAttachment={deleteAttachment}
            onFileAdd={onFileAdd}
            onEditorReady={handleEditorReady}
            mutateTicket={mutateTicket}
            setEditorHasChanged={setEditorHasChanged}
          />
        </EditorProvider>
      </Box>
    </Box>
  );
};

export default RichTextEditor;
